/**
 * Lista paginada de ProvisioningJobs (TASK-2436).
 *
 * - Filtro por status (Mantine Select, clearable)
 * - Paginação client-side via parâmetro offset (server lê pageSize=50)
 * - Click em row navega para /admin/tenants/jobs/:id
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Title,
  Group,
  Select,
  Pagination,
  Stack,
  Text,
  Alert,
} from '@mantine/core'
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react'
import { Anchor } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import {
  awsOrgProvisioningApi,
  type ProvisioningJobStatus,
} from '@/api/awsOrgProvisioning'
import { ProvisioningJobsTable } from '@/components/admin/tenants/jobs/ProvisioningJobsTable'

const PAGE_SIZE = 50

const STATUS_OPTIONS: ProvisioningJobStatus[] = [
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
]

export function ProvisioningJobsList() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['provisioning-jobs', statusFilter, page],
    queryFn: () =>
      awsOrgProvisioningApi.listJobs({
        status: (statusFilter as ProvisioningJobStatus | null) ?? undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
    // Polling 3s quando há jobs em execução; refetch em background tb (essencial).
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? []
      const hasActive = items.some(
        (j) => j.status === 'running' || j.status === 'pending',
      )
      return hasActive ? 3000 : false
    },
    refetchIntervalInBackground: true,
  })

  const totalPages =
    data && data.limit > 0 ? Math.max(1, Math.ceil(data.total / data.limit)) : 1

  return (
    <Container size="xl" data-testid="provisioning-jobs-list">
      <Anchor
        component="button"
        type="button"
        onClick={() => navigate('/admin/tenants')}
        c="dimmed"
        size="sm"
        mb="xs"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        data-testid="back-to-tenants"
      >
        <IconArrowLeft size={14} />
        Voltar para Tenants
      </Anchor>
      <Title order={2} mb="lg">
        Provisioning Jobs
      </Title>
      <Stack gap="md">
        <Group align="flex-end">
          <Select
            label="Status"
            placeholder="Todos"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value)
              setPage(1)
            }}
            data={STATUS_OPTIONS}
            clearable
            w={200}
            data-testid="jobs-status-filter"
          />
          <Text c="dimmed" size="sm" mb={4} data-testid="jobs-total">
            {data?.total ?? 0} jobs
          </Text>
        </Group>

        {error && (
          <Alert color="red" icon={<IconAlertCircle />} data-testid="jobs-error">
            Erro ao carregar jobs: {String(error)}
          </Alert>
        )}

        <ProvisioningJobsTable
          jobs={data?.items ?? []}
          loading={isLoading}
          onRowClick={(jobId) => navigate(`/admin/tenants/jobs/${jobId}`)}
        />

        {totalPages > 1 && (
          <Group justify="center">
            <Pagination
              value={page}
              onChange={setPage}
              total={totalPages}
              data-testid="jobs-pagination"
            />
          </Group>
        )}
      </Stack>
    </Container>
  )
}
